import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Card } from "components/ui/Card";
import { Button } from "components/ui/Button";
import { ApiKey, getMyApiKeys, revealApiKey, revokeMyApiKey } from "interfaces/apiKeys";
import { MyApiAccess, getMyApiAccess } from "interfaces/publicApi";
import env from "variables/config";

// The API's public address (the app's backend URL, without a trailing slash)
const apiBase = String(env.apiUrl || "https://api.duckflow.io").replace(/\/+$/, "");

async function copyText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // navigator.clipboard is unavailable outside secure contexts (plain http)
        const el = document.createElement("textarea");
        el.value = text;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(el);
        return ok;
    }
}

function formatDate(d?: string) {
    if (!d) return "Jamais";
    return new Date(d).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** The full key, hidden by default and fetched only when shown or copied. */
function SecretField({ keyId, initial }: { keyId: string; initial?: string }) {
    const [secret, setSecret] = useState(initial || "");
    const [visible, setVisible] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = async (): Promise<string | null> => {
        if (secret) return secret;
        const res = await revealApiKey(keyId);
        if (!res.secret) {
            toast.error(res.error || "Impossible de récupérer la clé");
            return null;
        }
        setSecret(res.secret);
        return res.secret;
    };

    const toggle = async () => {
        if (visible) return setVisible(false);
        if (await load()) setVisible(true);
    };

    const copy = async () => {
        const value = await load();
        if (!value) return;
        if (!(await copyText(value))) {
            toast.error("Impossible de copier la clé");
            return;
        }
        toast.success("Clé copiée");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="flex gap-2">
            <input
                type={visible ? "text" : "password"}
                readOnly
                // Placeholder dots until the real key is fetched
                value={visible ? secret : "•".repeat(48)}
                onFocus={(e) => visible && e.target.select()}
                className="flex-1 min-w-0 text-xs font-mono px-3 h-9 border border-border rounded-lg bg-surface-sunken text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent"
            />
            <Button size="sm" variant="secondary" onClick={toggle}>
                {visible ? "Masquer" : "Afficher"}
            </Button>
            <Button size="sm" onClick={copy}>{copied ? "Copié !" : "Copier"}</Button>
        </div>
    );
}

/**
 * Settings → Accès API. Keys are created by DuckFlow (/bo/api-keys), never by the user, so this card
 * only appears once the user has an active key: they can show / copy it and delete it, nothing else.
 */
export default function ApiKeysCard() {
    const [keys, setKeys] = useState<ApiKey[] | null>(null);
    const [access, setAccess] = useState<MyApiAccess | null>(null);

    const load = async () => {
        try {
            const res = await getMyApiKeys();
            setKeys(res.keys);
        } catch {
            setKeys([]);
        }
        getMyApiAccess()
            .then(setAccess)
            .catch(() => setAccess(null));
    };

    useEffect(() => {
        load();
    }, []);

    const activeKeys = (keys || []).filter((k) => !k.revokedAt);
    const revokedKeys = (keys || []).filter((k) => k.revokedAt);

    const handleRevoke = async (key: ApiKey) => {
        if (!window.confirm(`Supprimer la clé « ${key.name} » ? Les applications qui l'utilisent n'auront plus accès, et seul DuckFlow pourra vous en créer une nouvelle.`)) return;
        const res = await revokeMyApiKey(key._id);
        if (res.error) {
            toast.error(res.error);
            return;
        }
        toast.success("Clé supprimée");
        load();
    };

    // Hidden until DuckFlow has created a key for this user
    if (!activeKeys.length) return null;

    return (
        <Card>
            <h3 className="text-base font-semibold mb-2 text-ink">Accès API</h3>
            <p className="text-sm text-ink-muted mb-4">
                Votre clé, créée par DuckFlow, permet d'interroger l'API depuis vos propres outils (Postman, scripts, votre CRM) :{" "}
                <code className="text-xs bg-surface-sunken px-1 py-0.5 rounded">{apiBase}/api-client</code>, avec l'en-tête{" "}
                <code className="text-xs bg-surface-sunken px-1 py-0.5 rounded">Authorization: Bearer &lt;clé&gt;</code>. L'API est en <strong>lecture seule</strong>, à
                l'exception des signalements d'établissements. Gardez la clé côté serveur et ne la partagez pas.
            </p>

            {access && !access.enabled ? (
                <div className="mb-4 text-xs text-ink-muted bg-surface-sunken border border-border rounded-lg p-3">
                    L'accès API de votre compte a été désactivé : vos clés sont refusées pour l'instant. Contactez votre interlocuteur DuckFlow.
                </div>
            ) : (
                <div className="mb-4 text-xs text-ink-muted bg-surface-sunken border border-border rounded-lg p-3 space-y-1">
                    <p>
                        <span className="font-medium text-ink">Accès API actif.</span> Au plus {access?.maxPageSize ?? 20} résultats par appel (paramètre{" "}
                        <code className="bg-surface px-1 rounded">limit</code>), une requête à la fois.
                    </p>
                    <p>
                        Si un appel est refusé avec le code <code className="bg-surface px-1 rounded">429</code>, attendez le nombre de secondes indiqué par l'en-tête{" "}
                        <code className="bg-surface px-1 rounded">Retry-After</code> avant de rappeler, puis reprenez avec le même <code className="bg-surface px-1 rounded">next_cursor</code>.
                    </p>
                </div>
            )}

            <ul className="divide-y divide-border border border-border rounded-lg">
                {activeKeys.map((k) => (
                    <li key={k._id} className="p-3 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-ink truncate">
                                    {k.name} <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface-sunken text-ink-muted align-middle">API</span>
                                </p>
                                <p className="text-xs text-ink-muted">
                                    créée le {formatDate(k.createdAt)} · dernière utilisation : {formatDate(k.lastUsedAt)} · {k.usageCount.toLocaleString("fr-FR")} appel
                                    {k.usageCount > 1 ? "s" : ""}
                                </p>
                            </div>
                            <button onClick={() => handleRevoke(k)} className="text-xs text-danger hover:underline shrink-0">
                                Supprimer
                            </button>
                        </div>
                        <SecretField keyId={k._id} />
                    </li>
                ))}
            </ul>

            {revokedKeys.length > 0 && (
                <details className="mt-3">
                    <summary className="text-xs text-ink-muted cursor-pointer">Clés désactivées ({revokedKeys.length})</summary>
                    <ul className="mt-2 space-y-1">
                        {revokedKeys.map((k) => (
                            <li key={k._id} className="text-xs text-ink-muted">
                                <span className="line-through">{k.name}</span> <span className="font-mono">{k.prefix}…</span> —{" "}
                                {k.revokedBy === "user" ? "supprimée" : "désactivée par notre équipe"} le {formatDate(k.revokedAt)}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </Card>
    );
}
